// src/components/LanguageSwitcher.js
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

const languages = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "fr", label: "Français", flag: "🇫🇷" }
];

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Langue courante
  const currentLanguage =
    languages.find((l) => l.code === i18n.language) || languages[0];

  // Appliquer la langue sauvegardée au montage
  useEffect(() => {
    const saved = localStorage.getItem("serveandcode_language");
    if (saved && saved !== i18n.language && typeof i18n.changeLanguage === "function") {
      i18n.changeLanguage(saved);
    }
  }, [i18n]);

  // Fermer le dropdown si clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageChange = (code) => {
    if (typeof i18n.changeLanguage === "function") {
      i18n.changeLanguage(code);
    }
    localStorage.setItem("serveandcode_language", code);
    setIsOpen(false);
  };

  return (
    <div className="lang-switcher" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="lang-switcher__button"
      >
        <Globe className="lang-switcher__icon" />
        <span className="lang-switcher__flag">{currentLanguage.flag}</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="lang-switcher__dropdown"
          >
            {languages.map((language) => (
              <button
                key={language.code}
                type="button"
                onClick={() => handleLanguageChange(language.code)}
                className={
                  "lang-switcher__dropdown-item" +
                  (currentLanguage.code === language.code
                    ? " lang-switcher__dropdown-item--active"
                    : "")
                }
              >
                <div className="lang-switcher__dropdown-label">
                  <span className="lang-switcher__dropdown-flag">
                    {language.flag}
                  </span>
                  <span>{language.label}</span>
                </div>
                {currentLanguage.code === language.code && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  >
                    <Check className="lang-switcher__dropdown-check" />
                  </motion.div>
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LanguageSwitcher;
